import { featRules } from '../../../shared/rules/featSelection.mjs';
import { SKILLS } from '../skillSchema';

/**
 * The entry a chosen feat adds to the character's feat list, before its ability and skill choices: the catalogue's
 * fixed bonuses and skills. A non-repeatable feat the character already has keeps that entry's skill picks. Used by
 * the Feats window and the level-up feat choice, so both save the same shape.
 */
export const buildFeatEntry = (featObj, existingFeat) => {
  const entry = {
    featName: featObj.featName,
    notes: featObj.notes,
    initiative: featObj.initiative ?? 0,
    ac: featObj.ac ?? 0,
    speed: featObj.speed ?? 0,
    hpMaxBonus: featObj.hpMaxBonus ?? 0,
    hpMaxBonusPerLevel: featObj.hpMaxBonusPerLevel ?? 0,
  };
  SKILLS.forEach(({ key }) => {
    entry[key] = featObj[key];
  });
  ['str', 'dex', 'con', 'int', 'wis', 'cha'].forEach((ability) => {
    entry[ability] = featObj[ability] ?? 0;
  });
  entry.skills = {
    ...(featObj.skills || {}),
    ...(!featRules(featObj).repeatable ? existingFeat?.skills || {} : {}),
  };
  return entry;
};

const SKILL_CHOICE_FIELDS = ['skillChoiceCount', 'skillChoices', 'skillOptions', 'skillChoice', 'skillProficiencies',
  'toolChoiceCount', 'toolChoices', 'toolOptions', 'toolChoice', 'toolProficiencies'];
/** Whether the feat asks for skill or tool proficiency picks, and how many (at most 3). */
export const featSkillChoice = (featObj) => ({
  has: SKILL_CHOICE_FIELDS.some((field) => featObj?.[field]) || featObj?.featName === 'Skilled',
  limit: Math.min(featObj?.skillChoiceCount || featObj?.toolChoiceCount || 3, 3),
});

/** Applies the picked ability increases (one ability per abilityIncreaseOptions entry) to the entry's bonuses. */
export const withAbilityChoices = (entry, featObj, picks = {}) => {
  const bonus = Object.fromEntries(['str', 'dex', 'con', 'int', 'wis', 'cha'].map((key) => [key, Number(featObj?.[key] || 0)]));
  Object.entries(picks).forEach(([index, ability]) => {
    if (!ability) return;
    const option = featObj.abilityIncreaseOptions?.[index];
    bonus[ability] += Array.isArray(option) ? 1 : option?.amount ?? 1;
  });
  return { ...entry, ...bonus };
};
